<script setup lang="ts">
import type LxcTable from "@lxc/app-device-common/src/components/LxcTable.vue";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { useAcl } from "vue-simple-acl";
import { useToggle } from "~/core/composables/useToggle";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import { PATH_SECTORS_CODE_SECTORS } from "~/modules/sector/constants";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import { getSectorDepth } from "~/modules/sector/utils/getSectorDepth";

const { t } = useI18n();
const { can } = useAcl();
const router = useRouter();
const { sectors } = storeToRefs(useSectorStore());
const [removeDialogVisible, toggleRemoveDialogVisible] = useToggle();

const props = defineProps<{
  sector: Sector;
  isLoading: boolean;
  error: Error | null;
}>();
const emit = defineEmits(["remove"]);

const parentSectorCode: Ref<number | undefined> = ref();
const showSectorForm = ref(false);
const selectedSector = ref<Sector | undefined>();
const currentSearchQuery = ref("");
const searchQuery = ref("");

const sectorChildren = computed(() =>
  props.sector.children.map((sector) => {
    return { name: sector.name, code: sector.code, statuses: sector.statuses };
  }),
);

const filteredSectorChildren = computed(() => {
  if (!searchQuery.value) {
    return sectorChildren.value;
  }

  const query = searchQuery.value.toLowerCase();
  return sectorChildren.value.filter((sector) =>
    sector.name.toLowerCase().includes(query),
  );
});

const currentSectorDepth = computed(() => {
  return getSectorDepth(
    sectors.value ? [sectors.value] : [],
    props.sector.code,
  );
});

async function onClickSector(sector: Sector) {
  if (can(ACLRoles.CAN_READ_SECTOR)) {
    await router.push({
      name: PATH_SECTORS_CODE_SECTORS,
      params: { code: sector.code },
    });
  }
}

function addSector(sectorCode: number) {
  parentSectorCode.value = sectorCode;
  showSectorForm.value = true;
}

function onRemoveSector(sector: Sector) {
  selectedSector.value = sector;
  toggleRemoveDialogVisible();
}

function onCloseSectorForm() {
  showSectorForm.value = false;
}

function onCloseSectorRemove(removed?: boolean) {
  toggleRemoveDialogVisible();
  if (removed) {
    emit("remove", selectedSector.value);
  }
}

function search() {
  searchQuery.value = currentSearchQuery.value;
}
</script>
<template>
  <div class="mb-4 w-full">
    <search-bar
      v-model:search-query="currentSearchQuery"
      :search-placeholder="t('filters.searchByName')"
      @clear="search"
      @search="search"
    />
  </div>
  <lxc-table
    ref="tableRef"
    min-width="18rem"
    :is-loading="isLoading"
    :data="filteredSectorChildren"
    :empty-text="t('sectors.empty')"
    :error="error"
    :clickable="can(ACLRoles.CAN_READ_SECTOR)"
    @row-click="onClickSector"
  >
    <lxc-table-column
      prop="name"
      :label="t('table.header.label')"
      min-width="10rem"
    />
    <lxc-table-column
      v-if="can(ACLRoles.CAN_WRITE_SECTOR)"
      class="!py-0 !px-0"
      width="8rem"
    >
      <template #default="scope">
        <sector-detail-sectors-actions
          :sector="scope.row"
          :sector-depth="currentSectorDepth + 1"
          @add="addSector"
          @remove="onRemoveSector"
        />
      </template>
    </lxc-table-column>
  </lxc-table>

  <sector-form
    v-model:show="showSectorForm"
    :parent-sector-code="parentSectorCode"
    @close="onCloseSectorForm"
    @change="onCloseSectorForm"
  />

  <sector-remove
    :is-visible="removeDialogVisible"
    :sector="selectedSector"
    @close="onCloseSectorRemove()"
    @change="onCloseSectorRemove(true)"
  />
</template>
<style lang="scss" scoped>
:deep(table) {
  tbody {
    tr {
      &:hover {
        button {
          visibility: visible;
        }
      }
    }
  }
}
</style>
